import { useSimilarItems, Scenarios, GetRecommendationsFallback } from 'src/recommendations';
import { ProductCarouselProps } from 'src/components/product-carousel';
import { useDecorateWithSponsoredProducts } from 'src/utils/ads/hooks/sponsored-products';
import { SponsoredProductsInventoryId } from 'src/gql/graphql';
import { useLegacyCarouselsEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';

type UseRelatedItemsCarouselParams = {
  category: string;
  productId: string;
};

type UseRelatedItemsCarouselReturn = Omit<ProductCarouselProps, 'heading'>;

const SPONSORED_PRODUCTS_REQUEST_LIMIT = 10;

export function useRelatedItemsCarousel({
  category,
  productId,
}: UseRelatedItemsCarouselParams): UseRelatedItemsCarouselReturn {
  const adsEnabled = useAdsEnabled();
  const skipAds = !adsEnabled;
  const isLegacyCarouselsEnabled = useLegacyCarouselsEnabled();

  const getProductsFallback: GetRecommendationsFallback = (getProducts) =>
    getProducts({
      sortBy: `popularSortIdx`,
      types: [category],
    });

  const { data: organicRecommendations, loading: organicLoading } = useSimilarItems(
    getProductsFallback,
    productId,
    Scenarios.productDetailsPage1,
    {
      skip: !isLegacyCarouselsEnabled || !productId,
      limit: 25,
    }
  );

  const { products: sponsoredAndOrganicProducts, loading: sponsoredLoading } = useDecorateWithSponsoredProducts({
    skip: skipAds || !productId,
    products: organicRecommendations ?? [],
    loading: organicLoading,
    error: undefined,
    limit: SPONSORED_PRODUCTS_REQUEST_LIMIT,
    inventoryId: SponsoredProductsInventoryId.pdpProductAuc,
    searchQuery: undefined,
    productId,
  });

  const hideSponsoredTags = skipAds || !sponsoredAndOrganicProducts.some((product) => !!product.adTrackers);

  return {
    products: sponsoredAndOrganicProducts,
    loading: organicLoading || sponsoredLoading,
    placement: 'product-detail-page-related-items-carousel',
    hideSponsoredTags,
  };
}
